"use client";

/**
 * Formulaire de demande de prélèvement à domicile (POST /api/laboratoires/prelevement).
 *
 * Libellés reçus en props depuis le serveur (homeSamplingLabels, getTranslations
 * « labs.form ») : la sous-clé « form » n'est pas sérialisée dans le
 * NextIntlClientProvider de toutes les pages (CLIENT_SUBKEYS du layout).
 *
 * Validation par champ dans le navigateur (aria-invalid + aria-describedby,
 * focus sur le premier champ en erreur), erreurs du serveur rattachées au bon
 * champ ; en cas de succès, le message reçoit le focus.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "@/lib/i18n/navigation";
import { isValidMarPhone } from "@/lib/labs/format";

export type HomeSamplingLabels = {
  title: string; name: string; phone: string; phoneHint: string; city: string; cityPlaceholder: string; address: string;
  tests: string; testsPlaceholder: string; date: string; slot: string; slotMorning: string; slotAfternoon: string; slotAny: string;
  lab: string; consent: string; consentStorage: string; privacyLink: string; submit: string; sending: string; success: string;
  error: string; tooMany: string; nameRequired: string; phoneRequired: string; phoneInvalid: string; cityRequired: string;
  dateInvalid: string; consentRequired: string; fixErrors: string;
};

type Props = {
  locale: string;
  labels: HomeSamplingLabels;
  cities: Array<{ slug: string; name: string }>;
  defaultCitySlug?: string;
  lab?: { slug: string; name: string } | null;
};

type Field = "name" | "phone" | "citySlug" | "date" | "consent";
const ORDER: Field[] = ["name", "phone", "citySlug", "date", "consent"];
const FIELD_ID: Record<Field, string> = { name: "hs-name", phone: "hs-phone", citySlug: "hs-city", date: "hs-date", consent: "hs-consent" };

const field = "w-full rounded-[12px] border border-[var(--sahha-border-control)] bg-[var(--sahha-surface-0)] px-3.5 py-2.5 text-[15px] text-[var(--sahha-text-900)] placeholder:text-[var(--sahha-text-400)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-700)] aria-[invalid=true]:border-[var(--sahha-danger-text)]";
const labelCls = "mb-1.5 block text-[13.5px] font-semibold text-[var(--sahha-text-800)]";
const errCls = "mt-1 text-[12.5px] font-semibold text-[var(--sahha-danger-text)]";

export function HomeSamplingForm({ locale, labels: t, cities, defaultCitySlug, lab }: Props) {
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => { if (state === "sent") successRef.current?.focus(); }, [state]);

  const focusFirst = (errs: Partial<Record<Field, string>>) => {
    const first = ORDER.find((f) => errs[f]);
    if (first) (formRef.current?.querySelector(`#${FIELD_ID[first]}`) as HTMLElement | null)?.focus();
  };

  function validate(f: FormData): Partial<Record<Field, string>> {
    const errs: Partial<Record<Field, string>> = {};
    const name = String(f.get("name") ?? "").trim();
    const phone = String(f.get("phone") ?? "").trim();
    if (name.length < 2) errs.name = t.nameRequired;
    if (!phone) errs.phone = t.phoneRequired;
    else if (!isValidMarPhone(phone)) errs.phone = t.phoneInvalid;
    if (!String(f.get("citySlug") ?? "")) errs.citySlug = t.cityRequired;
    const date = String(f.get("date") ?? "");
    if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date)) errs.date = t.dateInvalid;
    if (!f.get("consent")) errs.consent = t.consentRequired;
    return errs;
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === "sending") return;
    const f = new FormData(e.currentTarget);
    const errs = validate(f);
    setErrors(errs);
    setFormError(null);
    if (Object.keys(errs).length) { focusFirst(errs); return; }
    setState("sending");
    try {
      const r = await fetch("/api/laboratoires/prelevement", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: f.get("name"), phone: f.get("phone"), citySlug: f.get("citySlug"), address: f.get("address"),
          tests: f.get("tests"), date: f.get("date"), slot: f.get("slot"), labSlug: lab?.slug ?? "",
          consent: true, locale, website: f.get("website"),
        }),
      });
      if (r.ok) { setState("sent"); return; }
      setState("idle");
      if (r.status === 429) { setFormError(t.tooMany); return; }
      const d = (await r.json().catch(() => ({}))) as { field?: string };
      const serverField: Record<string, Field> = { name: "name", phone: "phone", citySlug: "citySlug", date: "date", consent: "consent" };
      const target = d.field ? serverField[d.field] : undefined;
      if (r.status === 400 && target) {
        const msg = { name: t.nameRequired, phone: t.phoneInvalid, citySlug: t.cityRequired, date: t.dateInvalid, consent: t.consentRequired }[target];
        const errs2 = { [target]: msg };
        setErrors(errs2);
        focusFirst(errs2);
        return;
      }
      setFormError(t.error);
    } catch {
      setState("idle");
      setFormError(t.error);
    }
  }

  if (state === "sent") {
    return (
      <p ref={successRef} tabIndex={-1} role="status" className="rounded-[18px] bg-[var(--sahha-success-bg)] p-5 text-[15px] font-semibold text-[var(--sahha-text-900)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sahha-primary-700)]">
        {t.success}
      </p>
    );
  }

  const invalid = (k: Field) => (errors[k] ? { "aria-invalid": true as const, "aria-describedby": `${FIELD_ID[k]}-err` } : {});
  const errorOf = (k: Field) => errors[k] ? <p id={`${FIELD_ID[k]}-err`} className={errCls}>{errors[k]}</p> : null;
  const hasErrors = Object.keys(errors).length > 0;
  // Un champ corrigé perd son message d'erreur.
  const onChange = (e: React.FormEvent<HTMLFormElement>) => {
    const k = (e.target as HTMLInputElement).name as Field;
    if (errors[k]) setErrors((prev) => { const next = { ...prev }; delete next[k]; return next; });
  };

  return (
    <form ref={formRef} onSubmit={onSubmit} onChange={onChange} noValidate className="grid gap-4 rounded-[20px] bg-[var(--sahha-surface-0)] p-5 ring-1 ring-[var(--sahha-border-subtle)] sm:grid-cols-2 sm:p-6">
      <h2 className="text-[18px] font-bold text-[var(--sahha-text-900)] sm:col-span-2">{t.title}</h2>
      {lab && <p className="text-[14px] text-[var(--sahha-text-700)] sm:col-span-2">{t.lab} <strong>{lab.name}</strong></p>}
      <div>
        <label htmlFor="hs-name" className={labelCls}>{t.name}</label>
        <input id="hs-name" name="name" required minLength={2} maxLength={80} autoComplete="name" className={field} {...invalid("name")} />
        {errorOf("name")}
      </div>
      <div>
        <label htmlFor="hs-phone" className={labelCls}>{t.phone}</label>
        <input id="hs-phone" name="phone" type="tel" required inputMode="tel" autoComplete="tel" dir="ltr" placeholder="06 12 34 56 78" className={field}
          aria-invalid={errors.phone ? true : undefined} aria-describedby={errors.phone ? "hs-phone-err hs-phone-hint" : "hs-phone-hint"} />
        {errorOf("phone")}
        <p id="hs-phone-hint" className="mt-1 text-[12.5px] text-[var(--sahha-text-500)]">{t.phoneHint}</p>
      </div>
      <div>
        <label htmlFor="hs-city" className={labelCls}>{t.city}</label>
        <select id="hs-city" name="citySlug" required defaultValue={defaultCitySlug ?? ""} className={field} {...invalid("citySlug")}>
          <option value="" disabled>{t.cityPlaceholder}</option>
          {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        {errorOf("citySlug")}
      </div>
      <div><label htmlFor="hs-address" className={labelCls}>{t.address}</label><input id="hs-address" name="address" maxLength={160} autoComplete="street-address" className={field} /></div>
      <div className="sm:col-span-2"><label htmlFor="hs-tests" className={labelCls}>{t.tests}</label><textarea id="hs-tests" name="tests" rows={2} maxLength={500} placeholder={t.testsPlaceholder} className={field} /></div>
      <div>
        <label htmlFor="hs-date" className={labelCls}>{t.date}</label>
        <input id="hs-date" name="date" type="date" className={field} {...invalid("date")} />
        {errorOf("date")}
      </div>
      <fieldset>
        <legend className={labelCls}>{t.slot}</legend>
        <div className="flex flex-wrap gap-3 text-[14px] text-[var(--sahha-text-800)]">
          {(["morning", "afternoon", "any"] as const).map((s) => (
            <label key={s} className="inline-flex items-center gap-1.5">
              <input type="radio" name="slot" value={s} defaultChecked={s === "any"} className="accent-[var(--sahha-primary-700)]" />
              {s === "morning" ? t.slotMorning : s === "afternoon" ? t.slotAfternoon : t.slotAny}
            </label>
          ))}
        </div>
      </fieldset>
      {/* Pot de miel, masqué visuellement (pas display:none, que certains robots ignorent) et caché aux lecteurs d'écran. */}
      <div aria-hidden="true" className="sr-only">
        <label htmlFor="hs-website">Website</label>
        <input id="hs-website" type="text" name="website" tabIndex={-1} autoComplete="off" />
      </div>
      <div className="sm:col-span-2">
        <div className="flex items-start gap-2.5">
          <input id="hs-consent" type="checkbox" name="consent" required className="mt-1 h-4 w-4 shrink-0 accent-[var(--sahha-primary-700)]"
            aria-invalid={errors.consent ? true : undefined} aria-describedby={errors.consent ? "hs-consent-err hs-consent-info" : "hs-consent-info"} />
          <label htmlFor="hs-consent" className="text-[13.5px] leading-relaxed text-[var(--sahha-text-700)]">{t.consent}</label>
        </div>
        <p id="hs-consent-info" className="mt-1.5 ps-[26px] text-[12.5px] leading-relaxed text-[var(--sahha-text-600)]">
          {t.consentStorage}{" "}
          <Link href="/politique-confidentialite" className="font-semibold text-[var(--sahha-primary-800)] underline underline-offset-2">{t.privacyLink}</Link>
        </p>
        {errors.consent && <p id="hs-consent-err" className={`${errCls} ps-[26px]`}>{errors.consent}</p>}
      </div>
      {(formError || hasErrors) && (
        <p role="alert" className="text-[13.5px] font-semibold text-[var(--sahha-danger-text)] sm:col-span-2">{formError ?? t.fixErrors}</p>
      )}
      <div className="sm:col-span-2">
        <button type="submit" aria-disabled={state === "sending"} className="w-full rounded-full bg-[var(--sahha-primary-700)] px-6 py-3 text-[15px] font-bold text-white hover:bg-[var(--sahha-primary-800)] aria-disabled:opacity-70 sm:w-auto">
          {state === "sending" ? t.sending : t.submit}
        </button>
      </div>
    </form>
  );
}
